import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FreeMode, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import ElapsedTime from '../../../common/components/elapsed-time/ElapsedTime';
import { appSubscriptionService } from '../../../common/subscriptions/appSubscriptionService';
import { DispenseLog, DispenseState } from '../../api/dispenseLogs/DispenseLog';
import {
  useAcknowledgeDispenseLogVoidMutation,
  useGetReadyToServeDispenseLogsQuery,
  useUpdateDispenseLogStateMutation,
} from '../../api/dispenseLogs/dispenseLogsApiSlice';
import '../style.sass';
import './ready-to-serve.sass';

import 'swiper/css/bundle';

interface StatusMessage {
  id: number;
  type: 'success' | 'error';
  message: string;
}

const operatorsForLog = (items: { operatorName?: string }[]): string => {
  const names = Array.from(new Set(items.map((i) => i.operatorName).filter((n): n is string => !!n)));
  return names.length > 0 ? names.join(', ') : '-';
};

function ReadyToServe() {
  const navigate = useNavigate();
  const { data: dispenseLogs, isLoading, refetch } = useGetReadyToServeDispenseLogsQuery();
  const [updateDispenseLogState, { isLoading: isUpdating }] = useUpdateDispenseLogStateMutation();
  const [acknowledgeDispenseLogVoid, { isLoading: isAcknowledging }] = useAcknowledgeDispenseLogVoidMutation();
  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [pendingServeLog, setPendingServeLog] = useState<DispenseLog | null>(null);

  const readyLogs = dispenseLogs || [];

  const getErrorMessage = (error: unknown): string => {
    if (typeof error === 'object' && error !== null) {
      if ('data' in error) return String(error.data);
      if ('error' in error) return String(error.error);
    }
    return String(error);
  };

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleAcknowledgeVoid = async (log: DispenseLog) => {
    try {
      await acknowledgeDispenseLogVoid(log.id).unwrap();
      refetch();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setStatusMessages((prev) => [...prev, { id: log.id, type: 'error', message: errorMessage }]);
      setTimeout(() => {
        setStatusMessages((prev) => prev.filter((msg) => msg.id !== log.id));
      }, 5000);
    }
  };

  const handleServed = async (log: DispenseLog) => {
    try {
      await updateDispenseLogState({
        id: log.id,
        request: { state: DispenseState.Served },
      }).unwrap();
      setStatusMessages((prev) => [...prev, { id: log.id, type: 'success', message: 'Marked as served' }]);
      setTimeout(() => {
        setStatusMessages((prev) => prev.filter((msg) => msg.id !== log.id));
      }, 3000);
      refetch();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setStatusMessages((prev) => [...prev, { id: log.id, type: 'error', message: errorMessage }]);
      setTimeout(() => {
        setStatusMessages((prev) => prev.filter((msg) => msg.id !== log.id));
      }, 5000);
    }
  };

  useEffect(() => {
    const sub1 = appSubscriptionService.failedDispensePrintsChangedObservable().subscribe(() => {
      refetch();
    });
    const sub2 = appSubscriptionService.dispenseLogStateChangedObservable().subscribe(() => {
      refetch();
    });
    const sub3 = appSubscriptionService.dispenseCompleteObservable().subscribe(() => {
      refetch();
    });
    const sub4 = appSubscriptionService.dispenseLogVoidedObservable().subscribe(() => {
      refetch();
    });
    const sub5 = appSubscriptionService.dispenseLogVoidAcknowledgedObservable().subscribe(() => {
      refetch();
    });

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
      sub3.unsubscribe();
      sub4.unsubscribe();
      sub5.unsubscribe();
    };
  }, [refetch]);

  return (
    <div className="pos-modal-portal">
      <div
        className="pos-modal-overlay"
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((e.target as any).className === 'pos-modal-overlay') navigate(-1);
        }}
      >
        <div className="pos-modal pos-modal_role_ready-to-serve">
          <div className="pos-modal__header">
            <h2 className="pos-modal__title pos-modal__title_role_ready-to-serve">Ready to Serve</h2>
          </div>

          {pendingServeLog && (
            <div className="pos-modal__popup">
              <div className="pos-popup pos-popup_role_confirmation">
                <div className="pos-popup__content">
                  <div className="pos-popup__info">
                    <h3 className="pos-popup__title">Confirm</h3>
                    <p className="pos-popup__label">Mark as Served</p>
                    <p className="pos-popup__text">
                      #{pendingServeLog.transactionNumber} · Table {pendingServeLog.tableNumber || '-'}
                    </p>
                    <p className="pos-popup__text">
                      {operatorsForLog(pendingServeLog.items)} · {pendingServeLog.items.length} item
                      {pendingServeLog.items.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="pos-popup__actions">
                    <button className="pos-popup__action pos-popup__action_role_no" type="button" onClick={() => setPendingServeLog(null)}>
                      No
                    </button>
                    <button
                      className="pos-popup__action pos-popup__action_role_yes"
                      type="button"
                      disabled={isUpdating}
                      onClick={() => {
                        const log = pendingServeLog;
                        setPendingServeLog(null);
                        handleServed(log);
                      }}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="pos-modal__content">
            {isLoading && <div className="pos-modal__loading">Loading...</div>}

            {!isLoading && readyLogs.length === 0 && <div className="pos-modal__empty">No orders ready to serve</div>}

            {!isLoading && readyLogs.length > 0 && (
              <div className="pos-ready-to-serve">
                <div className="pos-ready-to-serve__scroll">
                  <Swiper
                    freeMode={true}
                    height={500}
                    slidesPerView="auto"
                    spaceBetween={0}
                    mousewheel={true}
                    scrollbar={true}
                    direction="vertical"
                    pagination={{ clickable: true }}
                    modules={[FreeMode, Pagination]}
                  >
                    <SwiperSlide>
                      <div className="pos-ready-to-serve__content">
                        <table className="pos-ready-to-serve__table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Transaction #</th>
                              <th>Table #</th>
                              <th>Device</th>
                              <th>Operator</th>
                              <th>Time</th>
                              <th>Items</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {readyLogs.map((log) => {
                              const statusMsg = statusMessages.find((msg) => msg.id === log.id);
                              const isExpanded = expandedRows.has(log.id);
                              return (
                                <>
                                  <tr
                                    key={log.id}
                                    className={`pos-ready-to-serve__row${log.isVoid ? ' pos-ready-to-serve__row_void' : ''}`}
                                  >
                                    <td>
                                      <button
                                        className="pos-ready-to-serve__expand-btn"
                                        onClick={() => toggleRow(log.id)}
                                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                      >
                                        {isExpanded ? '▼' : '▶'}
                                      </button>
                                    </td>
                                    <td>{log.transactionNumber}</td>
                                    <td>{log.tableNumber || '-'}</td>
                                    <td>{log.orderDeviceName}</td>
                                    <td>{operatorsForLog(log.items)}</td>
                                    <td>
                                      <ElapsedTime since={log.dateReadyToServe ?? log.dateCreated} />
                                    </td>
                                    <td>{log.items.length}</td>
                                    <td>
                                      {log.isVoid ? (
                                        <div className="pos-ready-to-serve__void-actions">
                                          <span className="pos-ready-to-serve__void-badge">VOID</span>
                                          <button
                                            className="pos-ready-to-serve__action pos-ready-to-serve__action_role_acknowledge"
                                            onClick={() => handleAcknowledgeVoid(log)}
                                            disabled={isAcknowledging}
                                          >
                                            Acknowledge
                                          </button>
                                        </div>
                                      ) : statusMsg ? (
                                        <div className={`pos-ready-to-serve__status pos-ready-to-serve__status_${statusMsg.type}`}>
                                          {statusMsg.message}
                                        </div>
                                      ) : (
                                        <button
                                          className="pos-ready-to-serve__action pos-ready-to-serve__action_role_served"
                                          onClick={() => setPendingServeLog(log)}
                                          disabled={isUpdating}
                                        >
                                          Served
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr key={`${log.id}-items`} className="pos-ready-to-serve__items-row">
                                      <td colSpan={8}>
                                        <div className="pos-ready-to-serve__items">
                                          <h4>Items:</h4>
                                          <ul className="pos-ready-to-serve__items-list">
                                            {log.items.map((item) => (
                                              <li
                                                key={item.id}
                                                className={`pos-ready-to-serve__item${item.isVoid ? ' pos-ready-to-serve__item_void' : ''}`}
                                              >
                                                <div className="pos-ready-to-serve__item-main">
                                                  <span className="pos-ready-to-serve__item-qty">{item.quantity}x</span>
                                                  <span className="pos-ready-to-serve__item-desc">{item.description}</span>
                                                  {item.isVoid && <span className="pos-ready-to-serve__item-void-label">VOID</span>}
                                                </div>
                                                {item.childItems && item.childItems.length > 0 && (
                                                  <ul className="pos-ready-to-serve__child-items">
                                                    {item.childItems.map((childItem, index) => (
                                                      <li key={`${item.id}-child-${index}`} className="pos-ready-to-serve__child-item">
                                                        <span className="pos-ready-to-serve__child-desc">{childItem.description}</span>
                                                      </li>
                                                    ))}
                                                  </ul>
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </SwiperSlide>
                  </Swiper>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReadyToServe;
